import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Award, Briefcase, GraduationCap, CheckCircle } from 'lucide-react';

const TeamSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center text-[#1F4E78]">
          {t('Leadership', 'Liderazgo')}
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          {t(
            'Founder-led execution with deep domain expertise and proven commercialization track record.',
            'Ejecución liderada por el fundador con profunda experiencia en el dominio y trayectoria probada de comercialización.'
          )}
        </p>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-3 gap-8 p-8">
            {/* Photo */}
            <div className="md:col-span-1">
              <div className="space-y-4">
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_med-innovation-2/artifacts/0gnivrte_IMG_7822.jpeg"
                    alt="Dr. Ángel Norro"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_med-innovation-2/artifacts/w8t0nc1j_IMG_6797.jpg"
                    alt="Dr. Ángel Norro in Surgery"
                    className="w-full h-auto object-cover"
                  />
                </div>
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_4a8b3dc5-324c-4ce6-a5c2-6f2d3b67502e/artifacts/va0o2no0_6018f805-a85c-41e4-9c05-c37d882cc01c.jpeg"
                    alt="Dr. Ángel Norro operating at HC Cancer Center Marbella"
                    className="w-full h-auto object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-xs text-gray-700 text-center">
                      {t(
                        'Dr. Norro operating at ',
                        'Dr. Norro operando en '
                      )}
                      <a 
                        href="https://www.hcmarbella.com/es/servicios/centro-del-cancer/" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#1F4E78] font-semibold hover:underline"
                      >
                        HC Hospital Cancer Center Marbella
                      </a>
                    </p>
                  </div>
                </div>
                <div className="rounded-lg overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets.emergentagent.com/job_4a8b3dc5-324c-4ce6-a5c2-6f2d3b67502e/artifacts/ntfocpjt_2591c295-28d6-4511-88bf-894a2bdc90f1.jpeg"
                    alt="Dr. Ángel Norro speaking at medical congress"
                    className="w-full h-auto object-cover"
                  />
                  <div className="bg-gray-100 p-3">
                    <p className="text-xs text-gray-700 text-center">
                      {t(
                        'Speaker at universities and medical congresses',
                        'Conferenciante en universidades y congresos médicos'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bio - Business Focused */}
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-1 text-[#1F4E78]">DR. ÁNGEL NORRO, MD</h3>
              <p className="text-lg text-[#2E5090] font-medium mb-4">
                {t('Founder & CEO', 'Fundador y CEO')}
              </p>
              <p className="text-gray-600 mb-6">
                {t(
                  'Clinician-scientist with 20+ years in oncology, metabolic targeting and clinical pharmacology. Inventor on the Portfolio Platinum patent family.',
                  'Científico-clínico con más de 20 años en oncología, direccionamiento metabólico y farmacología clínica. Inventor de la familia de patentes de Portfolio Platinum.'
                )}
              </p>

              {/* Key Qualifications Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-start space-x-3">
                  <GraduationCap className="text-[#1F4E78] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t('Domain Expertise', 'Experiencia de Dominio')}</p>
                    <p className="text-xs text-gray-600">{t('Oncology, metabolic targeting, pharmacology', 'Oncología, direccionamiento metabólico, farmacología')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Award className="text-[#1F4E78] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t('IP Track Record', 'Trayectoria en PI')}</p>
                    <p className="text-xs text-gray-600">{t('Multiple patents filed & granted', 'Múltiples patentes presentadas y concedidas')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Briefcase className="text-[#1F4E78] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t('Commercialization', 'Comercialización')}</p>
                    <p className="text-xs text-gray-600">{t('Licensed to selected clinics', 'Licenciado a clínicas escogidas')}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-[#1F4E78] flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{t('Self-Funded', 'Autofinanciado')}</p>
                    <p className="text-xs text-gray-600">{t('100% dedicated, no salary dependency', '100% dedicado, sin dependencia salarial')}</p>
                  </div>
                </div>
              </div>

              {/* Proven Commercialization */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <p className="text-sm font-semibold text-[#1F4E78] mb-2">
                  {t('Proven Medical Innovation → Commercialization', 'Innovación Médica → Comercialización Probada')}
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  {t(
                    'Creator of ',
                    'Creador de '
                  )}
                  <a href="https://www.newlip.es" target="_blank" rel="noopener noreferrer" className="text-[#1F4E78] font-bold hover:underline bg-blue-100 px-1 rounded">Newlip®</a>
                  {t(
                    ', a patented non-surgical rejuvenation method licensed to ',
                    ', método patentado de rejuvenecimiento sin cirugía licenciado a '
                  )}
                  <a href="https://academy.newlip.es" target="_blank" rel="noopener noreferrer" className="text-[#1F4E78] font-bold hover:underline bg-blue-100 px-1 rounded">
                    {t('selected clinics', 'clínicas escogidas')}
                  </a>
                  {t(
                    ' across Spain. Demonstrates ability to develop IP, validate clinically, and execute licensing strategy.',
                    ' en España. Demuestra capacidad para desarrollar PI, validar clínicamente y ejecutar estrategia de licenciamiento.'
                  )}
                </p>
                {/* Newlip Results Photo */}
                <div className="mt-3 flex justify-center">
                  <div className="rounded-md overflow-hidden w-48">
                    <img
                      src="https://cdn.durable.co/blocks/3523J4x3o8b9Bkilpb1EUliwalUtIjofuo0R4ePuarOxNwZmlYFhKDaOhnxqlShD.jpg"
                      alt="Newlip Results"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  {t('Newlip® before/after results', 'Resultados antes/después Newlip®')}
                </p>
              </div>

              {/* Team Building */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">{t('Team Expansion:', 'Expansión del Equipo:')}</span>{' '}
                  {t(
                    'Scientific, regulatory and business advisors being assembled in parallel with CRO engagement.',
                    'Asesores científicos, regulatorios y de negocios siendo ensamblados en paralelo con contratación de CRO.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;
